import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  CategoryKind,
  PlannedMovementKind,
  PlannedMovementStatus,
  RecurringDirection,
  RecurringSourceKind,
  TransactionType,
  type AccountWithBuckets,
  type Category,
  type PlannedMovement,
  type Transaction,
  type TransactionQuery,
  type Transfer as TransferItem,
  type UpcomingRecurring,
} from '@financial/shared';
import { useAccounts } from '../features/accounts/useAccounts';
import { useCategories } from '../features/categories/useCategories';
import {
  useDeleteTransaction,
  useTransaction,
  useTransactions,
} from '../features/transactions/useTransactions';
import { useDeleteTransfer, useTransfers } from '../features/transfers/useTransfers';
import { TransferForm } from '../features/transfers/TransferForm';
import { PlannedForm } from '../features/planned/PlannedForm';
import { useCancelPlanned, useConfirmPlanned, usePlanned } from '../features/planned/usePlanned';
import { useGenerateRecurringOne, useUpcomingRecurring } from '../features/recurring/useRecurring';
import {
  EditTransactionForm,
  QuickAddTransactionForm,
} from '../features/transactions/QuickAddTransaction';
import { formatDate, formatMoney, toDateInputValue } from '../lib/format';
import { getApiErrorMessage } from '../lib/errors';
import { Modal } from '../components/ui/Modal';
import { MonthPicker } from '../components/ui/DatePicker';
import {
  Button,
  EmptyState,
  FilterChipGroup,
  HelperText,
  Skeleton,
} from '../components/ui/Primitives';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { ItemActionsMenu } from '../components/ui/ItemActionsMenu';
import { Tooltip } from '../components/ui/Tooltip';
import { GuidedPageHeader } from '../components/onboarding/GuidedPageHeader';
import { SkeletonState } from '../components/ui/SkeletonState';
import { currentYearMonth } from '../lib/businessDate';
import { accountAvailableBalance } from '../lib/balancePreview';
import { ACCOUNT_TYPE_META } from '../lib/accountTypes';

function periodBounds(month: string): Pick<TransactionQuery, 'from' | 'to'> {
  const [year, monthIndex] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(year, monthIndex, 0)).getUTCDate();
  return {
    from: `${month}-01`,
    to: `${month}-${String(lastDay).padStart(2, '0')}`,
  };
}

function monthLabel(month: string): string {
  const [year, rawMonth] = month.split('-').map(Number);
  const name = new Intl.DateTimeFormat('es-MX', { month: 'long' }).format(
    new Date(year, rawMonth - 1, 1),
  );
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`;
}

function CategoryFilter({
  categories,
  selectedId,
  onChange,
}: {
  categories: Category[];
  selectedId?: string;
  onChange: (categoryId: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = categories.find((category) => category.id === selectedId);

  useEffect(() => {
    if (!open) return;
    const dismissOnOutsidePointer = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', dismissOnOutsidePointer);
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      document.removeEventListener('pointerdown', dismissOnOutsidePointer);
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [open]);

  const choose = (categoryId?: string) => {
    onChange(categoryId);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative min-w-0">
      <p className="mb-1.5 px-0.5 text-xs font-bold uppercase tracking-[.07em] text-slate-600">
        Categoría
      </p>
      <div className="flex h-[52px]">
        <button
          type="button"
          className={`tap-target flex h-full min-w-0 flex-1 items-center justify-between gap-2 border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 ${selected ? 'rounded-l-xl' : 'rounded-xl'}`}
          aria-haspopup="true"
          aria-expanded={open}
          aria-controls="transaction-category-options"
          onClick={() => setOpen((isOpen) => !isOpen)}
        >
          <span className="min-w-0 truncate">
            {selected ? `${selected.icon ? `${selected.icon} ` : ''}${selected.name}` : 'Todas'}
          </span>
          <span aria-hidden className="text-slate-500">
            ▾
          </span>
        </button>
        {selected && (
          <button
            type="button"
            className="tap-target -ml-px flex h-full w-10 items-center justify-center rounded-r-xl border border-slate-200 bg-slate-50 text-lg leading-none text-slate-600 transition hover:bg-slate-100 hover:text-slate-800"
            aria-label={`Quitar filtro ${selected.name}`}
            onClick={() => onChange(undefined)}
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <div
          id="transaction-category-options"
          className="absolute right-0 z-30 mt-2 w-[min(22rem,calc(100vw-3rem))] rounded-xl border border-slate-200 bg-white p-2 shadow-[0_18px_36px_-16px_rgba(15,23,42,.35)]"
          role="group"
          aria-label="Filtrar por categoría"
        >
          <p className="px-1.5 pb-2 pt-1 text-xs font-bold uppercase tracking-[.07em] text-slate-600">
            Filtrar por categoría
          </p>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              aria-pressed={!selectedId}
              onClick={() => choose()}
              className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${!selectedId ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              Todas
            </button>
            {categories.map((category) => {
              const active = category.id === selectedId;
              return (
                <button
                  key={category.id}
                  type="button"
                  aria-pressed={active}
                  onClick={() => choose(category.id)}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${active ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  {category.icon ? `${category.icon} ` : ''}
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export function TransactionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedTransactionId = searchParams.get('highlight') || null;
  const requestedPending =
    searchParams.get('mostrar') === 'pendientes' && !highlightedTransactionId;
  const [month, setMonth] = useState(currentYearMonth);
  const [allTime, setAllTime] = useState(Boolean(highlightedTransactionId));
  const [type, setType] = useState<TransactionType | undefined>();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<string | undefined>();
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Los traspasos no son ingresos ni egresos: se listan aparte.
  const [showTransfers, setShowTransfers] = useState(false);
  const [showPlanned, setShowPlanned] = useState(requestedPending);
  const [addOpen, setAddOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [plannedOpen, setPlannedOpen] = useState(false);
  const [editingPlanned, setEditingPlanned] = useState<PlannedMovement | null>(null);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const [transferToDelete, setTransferToDelete] = useState<TransferItem | null>(null);
  const [recurringToRegister, setRecurringToRegister] = useState<UpcomingRecurring | null>(null);
  const [plannedToConfirm, setPlannedToConfirm] = useState<PlannedMovement | null>(null);
  const [plannedToCancel, setPlannedToCancel] = useState<PlannedMovement | null>(null);
  const [activeHighlightId, setActiveHighlightId] = useState<string | null>(
    highlightedTransactionId,
  );
  const highlightedRowRef = useRef<HTMLLIElement>(null);
  const filter = useMemo<TransactionQuery>(
    () => ({
      limit: 100,
      type,
      categoryId,
      q: search || undefined,
      ...(allTime ? {} : periodBounds(month)),
    }),
    [allTime, categoryId, month, search, type],
  );
  const txQ = useTransactions(filter);
  const highlightedTransactionQ = useTransaction(highlightedTransactionId);
  const transfersQ = useTransfers({ limit: 100, ...(allTime ? {} : periodBounds(month)) });
  const delTransfer = useDeleteTransfer();
  // Esta vista solo representa lo que aún espera confirmación. Lo confirmado
  // ya vive en la lista de movimientos registrados y afectó sus saldos.
  const plannedQ = usePlanned({
    month,
    status: PlannedMovementStatus.PLANNED,
  });
  const upcomingRecurringQ = useUpcomingRecurring(month);
  const confirmPlanned = useConfirmPlanned();
  const cancelPlanned = useCancelPlanned();
  const generateRecurring = useGenerateRecurringOne();
  const accountsQ = useAccounts();
  const categoriesQ = useCategories();
  const del = useDeleteTransaction();

  useEffect(() => {
    if (searchParams.get('accion') !== 'nuevo') return;
    setAddOpen(true);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('accion');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const selected = (categoriesQ.data ?? []).find((category) => category.id === categoryId);
    if (
      selected &&
      type &&
      selected.kind !==
        (type === TransactionType.INCOME ? CategoryKind.INCOME : CategoryKind.EXPENSE)
    ) {
      setCategoryId(undefined);
    }
  }, [categoriesQ.data, categoryId, type]);

  // Permite llegar directamente a la bandeja desde el botón global, sin convertir
  // fijos pendientes en movimientos registrados por accidente.
  useEffect(() => {
    if (highlightedTransactionId) return;
    setShowPlanned(requestedPending);
    if (!requestedPending) return;
    setShowTransfers(false);
    setType(undefined);
    setAllTime(false);
  }, [highlightedTransactionId, requestedPending]);

  // Un enlace desde una propuesta aplicada debe abrir la lista real, aunque el
  // movimiento pertenezca a otro mes o hubiera filtros activos en esta vista.
  useEffect(() => {
    if (!highlightedTransactionId) {
      setActiveHighlightId(null);
      return;
    }
    setActiveHighlightId(highlightedTransactionId);
    setShowPlanned(false);
    setShowTransfers(false);
    setType(undefined);
    setCategoryId(undefined);
    setSearchInput('');
    setSearch('');
    setAllTime(true);
  }, [highlightedTransactionId]);

  const changeDisplay = (next: 'all' | 'transfers' | 'planned' | TransactionType) => {
    setShowTransfers(next === 'transfers');
    setShowPlanned(next === 'planned');
    if (next === 'planned') setAllTime(false);
    setType(next === 'all' || next === 'transfers' || next === 'planned' ? undefined : next);
    setSearchParams(
      (current) => {
        const nextParams = new URLSearchParams(current);
        if (next === 'planned') nextParams.set('mostrar', 'pendientes');
        else nextParams.delete('mostrar');
        nextParams.delete('highlight');
        return nextParams;
      },
      { replace: true },
    );
  };

  const accountName = useMemo(() => {
    const map = new Map<string, string>();
    (accountsQ.data ?? []).forEach((a) => map.set(a.id, a.name));
    return map;
  }, [accountsQ.data]);
  const categoryLabel = useMemo(() => {
    const map = new Map<string, string>();
    (categoriesQ.data ?? []).forEach((c) => map.set(c.id, `${c.icon ?? ''} ${c.name}`.trim()));
    return map;
  }, [categoriesQ.data]);
  const filterCategories = useMemo(
    () =>
      (categoriesQ.data ?? [])
        .filter(
          (category) =>
            !type ||
            category.kind ===
              (type === TransactionType.INCOME ? CategoryKind.INCOME : CategoryKind.EXPENSE),
        )
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX')),
    [categoriesQ.data, type],
  );

  const txs = useMemo(() => {
    const listed = txQ.data ?? [];
    const highlighted = highlightedTransactionQ.data;
    if (!highlighted || listed.some((transaction) => transaction.id === highlighted.id)) {
      return listed;
    }
    return [highlighted, ...listed];
  }, [highlightedTransactionQ.data, txQ.data]);
  const transfers = transfersQ.data ?? [];
  const pendingCount = (plannedQ.data?.length ?? 0) + (upcomingRecurringQ.data?.items.length ?? 0);
  const highlightedTransactionFound = Boolean(
    highlightedTransactionId &&
    txs.some((transaction) => transaction.id === highlightedTransactionId),
  );
  const highlightedTransactionLoading = Boolean(
    highlightedTransactionId && (txQ.isLoading || highlightedTransactionQ.isLoading),
  );
  const transactionsLoading =
    txQ.isLoading || Boolean(highlightedTransactionId && highlightedTransactionQ.isLoading);
  const activeAdvancedFilterCount = [Boolean(searchInput.trim()), Boolean(categoryId)].filter(
    Boolean,
  ).length;

  useEffect(() => {
    if (!highlightedTransactionId || highlightedTransactionLoading || !highlightedTransactionFound)
      return;
    const row = highlightedRowRef.current;
    if (!row) return;
    setActiveHighlightId(highlightedTransactionId);
    const frame = window.requestAnimationFrame(() => {
      row.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
      row.focus({ preventScroll: true });
    });
    const timeout = window.setTimeout(() => {
      setActiveHighlightId((current) => (current === highlightedTransactionId ? null : current));
    }, 4_000);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [highlightedTransactionFound, highlightedTransactionId, highlightedTransactionLoading]);

  const transactionSummary = useMemo(() => {
    const income = txs
      .filter(
        (transaction) => transaction.type === TransactionType.INCOME && !transaction.loanPaymentId,
      )
      .reduce((sum, transaction) => sum + transaction.amount, 0);
    const expense = txs
      .filter(
        (transaction) => transaction.type === TransactionType.EXPENSE && !transaction.loanPaymentId,
      )
      .reduce((sum, transaction) => sum + transaction.amount, 0);
    return { income, expense, net: income - expense, count: txs.length };
  }, [txs]);

  return (
    <div className="reference-page w-full">
      <GuidedPageHeader
        section="transactions"
        title="Movimientos"
        description={
          allTime
            ? 'Todo tu historial, ordenado de más reciente a más antiguo.'
            : `${monthLabel(month)} · consulta y corrige tus registros.`
        }
        actions={
          <div className="flex gap-2">
            <Tooltip
              content="Mueve dinero entre tus cuentas o apartados; no cuenta como ingreso ni gasto."
              side="bottom"
              align="end"
            >
              <Button onClick={() => setTransferOpen(true)} variant="secondary">
                <span className="text-lg font-normal leading-none" aria-hidden>
                  ⇄
                </span>{' '}
                Traspaso
              </Button>
            </Tooltip>
            <Tooltip content="Añade un ingreso o gasto que ya ocurrió." side="bottom" align="end">
              <Button onClick={() => setAddOpen(true)}>
                <span className="text-lg font-normal leading-none" aria-hidden>
                  +
                </span>{' '}
                Registrar
              </Button>
            </Tooltip>
          </div>
        }
      />

      <section className="mb-3 w-full sm:max-w-sm" aria-label="Período de movimientos">
        <MonthPicker
          value={month}
          allTime={allTime}
          onChange={(nextMonth) => {
            setMonth(nextMonth);
            setAllTime(false);
          }}
          onAllTime={showPlanned ? undefined : () => setAllTime(true)}
        />
      </section>

      <FilterChipGroup
        value={showPlanned ? 'planned' : showTransfers ? 'transfers' : (type ?? 'all')}
        ariaLabel="Vista de movimientos"
        options={[
          { label: 'Registrados', value: 'all' },
          { label: 'Ingresos', value: TransactionType.INCOME },
          { label: 'Gastos', value: TransactionType.EXPENSE },
          { label: 'Traspasos', value: 'transfers' },
          { label: 'Pendientes', value: 'planned', count: pendingCount },
        ]}
        onChange={changeDisplay}
        className="mb-3"
      />

      {!showTransfers && !showPlanned && (
        <button
          type="button"
          onClick={() => setFiltersOpen((current) => !current)}
          className="button button-secondary mb-3 w-full justify-between sm:hidden"
          aria-expanded={filtersOpen}
          aria-controls="transaction-filters"
        >
          <span>Más filtros</span>
          <span className="flex items-center gap-2 text-xs text-slate-600">
            <span
              className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-slate-100 px-1.5 font-bold text-slate-700"
              aria-label={`${activeAdvancedFilterCount} ${activeAdvancedFilterCount === 1 ? 'filtro avanzado activo' : 'filtros avanzados activos'}`}
            >
              {activeAdvancedFilterCount}
            </span>
            <span aria-hidden>{filtersOpen ? '▴' : '▾'}</span>
          </span>
        </button>
      )}

      {!showTransfers && !showPlanned && (
        <section
          id="transaction-filters"
          className={`transaction-filter-card ui-card mb-5 p-3 ${filtersOpen ? 'block' : 'hidden sm:block'}`}
        >
          <div className="grid gap-3 sm:grid-cols-[minmax(240px,1fr)_auto] sm:items-end">
            <label className="block min-w-0">
              <span className="mb-1.5 block px-0.5 text-xs font-bold uppercase tracking-[.07em] text-slate-600">
                Buscar
              </span>
              <span className="relative block">
                <svg
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden
                >
                  <circle cx="11" cy="11" r="6" />
                  <path d="m16 16 4 4" />
                </svg>
                <input
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Buscar por concepto"
                  aria-label="Buscar por concepto"
                  autoComplete="off"
                  className="tap-target h-[52px] w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-9 text-base text-slate-900 outline-none transition placeholder:text-slate-500 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput('');
                      setSearch('');
                    }}
                    className="tap-target absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg text-lg leading-none text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                    aria-label="Limpiar búsqueda"
                  >
                    ×
                  </button>
                )}
              </span>
            </label>
            <CategoryFilter
              categories={filterCategories}
              selectedId={categoryId}
              onChange={setCategoryId}
            />
          </div>
        </section>
      )}

      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_236px] lg:items-start lg:gap-4">
        <div className="min-w-0" data-testid="transactions-list-column">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-2 px-1 sm:items-center">
            <p className="min-w-0 flex-1 text-sm font-semibold leading-5 text-slate-700">
              {showPlanned
                ? allTime
                  ? 'Movimientos pendientes por confirmar'
                  : `Pendientes por confirmar de ${monthLabel(month)}`
                : showTransfers
                  ? allTime
                    ? 'Todos los traspasos'
                    : `Traspasos de ${monthLabel(month)}`
                  : allTime
                    ? 'Movimientos registrados'
                    : `Movimientos registrados de ${monthLabel(month)}`}
            </p>
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              {showPlanned && (
                <Button variant="secondary" className="px-3" onClick={() => setPlannedOpen(true)}>
                  <span className="text-base font-normal leading-none">+</span> Planear
                </Button>
              )}
              {!showTransfers && !showPlanned && !txQ.isLoading && (
                <p className="text-xs text-slate-600">
                  {txs.length} {txs.length === 1 ? 'registrado' : 'registrados'}
                </p>
              )}
              {showPlanned && !plannedQ.isLoading && !upcomingRecurringQ.isLoading && (
                <p className="text-xs text-slate-600">{pendingCount} pendientes</p>
              )}
            </div>
          </div>

          {showTransfers ? (
            <TransfersSummary transfers={transfers} loading={transfersQ.isLoading} />
          ) : showPlanned ? (
            <PendingSummary
              plannedItems={plannedQ.data ?? []}
              recurring={upcomingRecurringQ.data}
              loading={plannedQ.isLoading || upcomingRecurringQ.isLoading}
            />
          ) : (
            (transactionsLoading || txs.length > 0) && (
              <TransactionsSummary
                summary={transactionSummary}
                type={type}
                loading={txQ.isLoading}
                className="lg:hidden"
              />
            )
          )}

          {showPlanned && (
            <PendingMovementSections
              recurringItems={upcomingRecurringQ.data?.items ?? []}
              recurringLoading={upcomingRecurringQ.isLoading}
              plannedItems={plannedQ.data ?? []}
              plannedLoading={plannedQ.isLoading}
              accountName={accountName}
              onRegisterRecurring={setRecurringToRegister}
              registeringRecurring={generateRecurring.isPending}
              onConfirm={setPlannedToConfirm}
              onCancel={setPlannedToCancel}
              onEditPlanned={setEditingPlanned}
              confirmError={
                confirmPlanned.isError ? getApiErrorMessage(confirmPlanned.error) : undefined
              }
              onCreate={() => setPlannedOpen(true)}
              month={month}
            />
          )}

          {showTransfers && !showPlanned && (
            <TransfersList
              transfers={transfers}
              loading={transfersQ.isLoading}
              accountName={accountName}
              onDelete={setTransferToDelete}
            />
          )}

          {!showTransfers && !showPlanned && transactionsLoading && <TransactionSkeleton />}
          {!showTransfers &&
            !showPlanned &&
            highlightedTransactionId &&
            !highlightedTransactionLoading &&
            highlightedTransactionQ.isError &&
            !highlightedTransactionFound && (
              <div
                role="alert"
                className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900"
              >
                <p>
                  {getApiErrorMessage(
                    highlightedTransactionQ.error,
                    'No pudimos abrir el movimiento aplicado.',
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => void highlightedTransactionQ.refetch()}
                  className="tap-target mt-3 rounded-xl border border-red-200 bg-white px-3 font-semibold text-red-900 hover:bg-red-50"
                >
                  Volver a intentar
                </button>
              </div>
            )}
          {!showTransfers &&
            !showPlanned &&
            highlightedTransactionId &&
            !highlightedTransactionLoading &&
            !highlightedTransactionQ.isError &&
            !highlightedTransactionFound && (
              <p
                role="status"
                className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
              >
                El movimiento se aplicó, pero ya no está disponible en tu historial.
              </p>
            )}
          {!showTransfers &&
            !showPlanned &&
            !highlightedTransactionId &&
            txs.length === 0 &&
            !transactionsLoading && (
              <EmptyState
                title="No hay movimientos en este período"
                description={
                  search || categoryId
                    ? 'Prueba otro concepto o quita los filtros aplicados.'
                    : 'Prueba otro mes o registra tu primer ingreso o gasto.'
                }
                action={
                  !search && !categoryId ? (
                    <Button onClick={() => setAddOpen(true)}>Registrar movimiento</Button>
                  ) : undefined
                }
              />
            )}

          {!showTransfers && !showPlanned && txs.length > 0 && !transactionsLoading && (
            <div className="hidden lg:grid lg:grid-cols-[78px_minmax(160px,1.4fr)_minmax(120px,1fr)_minmax(110px,.9fr)_90px_70px] lg:items-center lg:gap-3 lg:rounded-t-[16px] lg:border lg:border-b-0 lg:border-slate-200/80 lg:bg-slate-50 lg:px-4 lg:py-3 lg:text-[10px] lg:font-bold lg:uppercase lg:tracking-[.08em] lg:text-slate-500">
              <span>Fecha</span>
              <span>Concepto</span>
              <span>Categoría</span>
              <span>Cuenta</span>
              <span className="text-right">Monto</span>
              <span className="sr-only">Acciones</span>
            </div>
          )}

          <ul
            className={`space-y-2.5 lg:overflow-hidden lg:rounded-[16px] lg:border lg:border-slate-200/80 lg:bg-white lg:shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)] ${!showTransfers && !showPlanned && txs.length > 0 && !transactionsLoading ? 'lg:rounded-t-none' : ''}`}
          >
            {(showTransfers || showPlanned || transactionsLoading ? [] : txs).map((t) => {
              const isIncome = t.type === TransactionType.INCOME;
              const category = t.categoryId
                ? (categoryLabel.get(t.categoryId) ?? 'Sin categoría')
                : 'Sin categoría';
              const account = accountName.get(t.accountId) ?? 'Cuenta';
              const highlighted = activeHighlightId === t.id;
              return (
                <li
                  key={t.id}
                  ref={t.id === highlightedTransactionId ? highlightedRowRef : undefined}
                  tabIndex={t.id === highlightedTransactionId ? -1 : undefined}
                  data-transaction-id={t.id}
                  data-highlighted={highlighted ? 'true' : undefined}
                  className={`relative grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1 rounded-[16px] border border-slate-200/80 bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)] transition-[background-color,border-color,box-shadow] duration-500 focus:outline-none lg:grid-cols-[78px_minmax(160px,1.4fr)_minmax(120px,1fr)_minmax(110px,.9fr)_90px_70px] lg:items-center lg:gap-3 lg:rounded-none lg:border-x-0 lg:border-t-0 lg:border-b-slate-100 lg:shadow-none lg:last:border-b-0 ${!t.reconciliationProtected ? 'cursor-pointer hover:bg-slate-50' : ''} ${highlighted ? '!border-blue-400 !bg-blue-50 ring-4 ring-blue-100 lg:ring-inset' : ''}`}
                >
                  {highlighted && <span className="sr-only">Movimiento recién aplicado.</span>}
                  {!t.reconciliationProtected && (
                    <button
                      type="button"
                      onClick={() => setEditingTransaction(t)}
                      className="absolute inset-0 z-0 rounded-[16px] outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset lg:rounded-none"
                      aria-label={`Abrir ${t.note ?? category} para editar`}
                    />
                  )}
                  <div
                    data-transaction-main
                    className="pointer-events-none relative z-[1] col-start-1 row-span-2 min-w-0 lg:hidden"
                  >
                    <p className="truncate text-sm font-semibold text-slate-800">
                      {category}
                      {t.note ? ` · ${t.note}` : ''}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-600">
                      {formatDate(t.date)} · {account}
                      {t.recurringExpenseId && ' · movimiento fijo'}
                      {t.loanPaymentId &&
                        (isIncome
                          ? ' · cobro de préstamo; no cuenta como ingreso'
                          : ' · pago de deuda; no cuenta como gasto')}
                      {t.reconciliationProtected &&
                        ' · conciliación protegida; corrige con un movimiento nuevo'}
                    </p>
                  </div>
                  <p className="pointer-events-none relative z-[1] hidden text-xs text-slate-500 lg:block">
                    {formatDate(t.date)}
                  </p>
                  <div className="pointer-events-none relative z-[1] hidden min-w-0 lg:block">
                    <p className="truncate text-sm font-semibold text-slate-800">
                      {t.note || category}
                    </p>
                    {t.reconciliationProtected ? (
                      <p className="truncate text-xs text-slate-500">
                        Conciliación protegida · registra una corrección nueva
                      </p>
                    ) : t.loanPaymentId ? (
                      <p className="truncate text-xs text-slate-500">
                        {isIncome
                          ? 'Cobro de préstamo · no cuenta como ingreso'
                          : 'Pago de deuda · no cuenta como gasto'}
                      </p>
                    ) : null}
                  </div>
                  <p className="pointer-events-none relative z-[1] hidden truncate text-xs text-slate-600 lg:block">
                    {category}
                  </p>
                  <p className="pointer-events-none relative z-[1] hidden truncate text-xs text-slate-500 lg:block">
                    {account}
                  </p>
                  <span
                    data-transaction-amount
                    className={`pointer-events-none relative z-[1] col-start-2 row-start-1 shrink-0 whitespace-nowrap text-right text-sm font-semibold tabular-nums lg:col-start-5 lg:row-auto ${isIncome ? 'text-income' : 'text-expense'}`}
                  >
                    {isIncome ? '+' : '−'}
                    {formatMoney(t.amount)}
                  </span>
                  <div
                    data-transaction-actions
                    className="relative z-[2] col-start-2 row-start-2 flex min-h-11 items-center justify-end gap-1 lg:col-start-6 lg:row-auto"
                  >
                    {t.reconciliationProtected ? (
                      <span
                        className="inline-flex min-h-11 items-center px-1 text-[11px] font-semibold text-savings"
                        title="Este registro explica una conciliación. Para corregirlo, registra un movimiento nuevo."
                      >
                        Conciliación
                      </span>
                    ) : t.loanPaymentId ? (
                      <Tooltip content="Revisar la deuda o préstamo relacionado" align="end">
                        <Link
                          to="/deudas"
                          className="tap-target inline-flex h-11 items-center px-2 text-xs font-semibold text-savings hover:text-blue-700"
                        >
                          {isIncome ? 'Préstamo' : 'Deuda'}
                        </Link>
                      </Tooltip>
                    ) : t.recurringExpenseId ? (
                      <Tooltip
                        content="Cambiar este mes o los próximos desde Movimientos fijos"
                        align="end"
                      >
                        <Link
                          to={`/gastos-fijos?ajustar=${encodeURIComponent(t.recurringExpenseId)}&mes=${t.recurringOccurrenceMonth ?? t.date.slice(0, 7)}`}
                          className="tap-target inline-flex h-11 items-center px-2 text-xs font-semibold text-savings hover:text-blue-700"
                          aria-label={`Cambiar monto del movimiento fijo ${t.note ?? ''}`.trim()}
                        >
                          Fijo
                        </Link>
                      </Tooltip>
                    ) : (
                      <Tooltip content="Eliminar movimiento" align="end">
                        <button
                          onClick={() => setTransactionToDelete(t)}
                          className="tap-target flex h-11 w-11 items-center justify-center rounded-lg text-slate-600 hover:bg-red-50 hover:text-expense"
                          aria-label="Eliminar"
                        >
                          ×
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="hidden space-y-3 lg:block" data-testid="transactions-desktop-rail">
          {!showTransfers && !showPlanned && (
            <TransactionsDesktopSummary summary={transactionSummary} loading={txQ.isLoading} />
          )}
          <AccountsDesktopPreview
            accounts={accountsQ.data ?? []}
            loading={accountsQ.isLoading}
            error={accountsQ.isError}
            onRetry={() => void accountsQ.refetch()}
          />
        </div>
      </div>

      <Modal open={addOpen} title="Nuevo movimiento" onClose={() => setAddOpen(false)}>
        <QuickAddTransactionForm onDone={() => setAddOpen(false)} />
      </Modal>
      <Modal open={plannedOpen} title="Planear movimiento" onClose={() => setPlannedOpen(false)}>
        <PlannedForm onDone={() => setPlannedOpen(false)} />
      </Modal>
      <Modal
        open={editingPlanned !== null}
        title="Editar movimiento planeado"
        onClose={() => setEditingPlanned(null)}
      >
        {editingPlanned && (
          <PlannedForm item={editingPlanned} onDone={() => setEditingPlanned(null)} />
        )}
      </Modal>
      <Modal open={transferOpen} title="Nuevo traspaso" onClose={() => setTransferOpen(false)}>
        <TransferForm onDone={() => setTransferOpen(false)} />
      </Modal>
      <Modal
        open={editingTransaction !== null}
        title={
          editingTransaction?.recurringExpenseId
            ? 'Corregir movimiento fijo de este mes'
            : 'Editar movimiento'
        }
        onClose={() => setEditingTransaction(null)}
      >
        {editingTransaction && (
          <EditTransactionForm
            transaction={editingTransaction}
            onDone={() => setEditingTransaction(null)}
          />
        )}
      </Modal>
      <ConfirmDialog
        open={transactionToDelete !== null}
        title="¿Eliminar movimiento?"
        description="Se eliminará del historial, de los saldos de la cuenta y de los cálculos del período."
        itemLabel={
          transactionToDelete
            ? `${transactionToDelete.type === TransactionType.INCOME ? '+' : '−'}${formatMoney(transactionToDelete.amount)} · ${transactionToDelete.note ?? 'Sin nota'}`
            : undefined
        }
        pending={del.isPending}
        error={del.isError ? getApiErrorMessage(del.error) : undefined}
        onClose={() => setTransactionToDelete(null)}
        onConfirm={() =>
          transactionToDelete &&
          del.mutate(transactionToDelete.id, { onSuccess: () => setTransactionToDelete(null) })
        }
      />
      <ConfirmDialog
        open={transferToDelete !== null}
        title="¿Eliminar traspaso?"
        description="El importe volverá a los saldos anteriores de ambas cuentas."
        itemLabel={
          transferToDelete
            ? `${formatMoney(transferToDelete.amount)} · ${accountName.get(transferToDelete.fromAccountId) ?? 'Cuenta'} → ${accountName.get(transferToDelete.toAccountId) ?? 'Cuenta'}`
            : undefined
        }
        pending={delTransfer.isPending}
        error={delTransfer.isError ? getApiErrorMessage(delTransfer.error) : undefined}
        onClose={() => setTransferToDelete(null)}
        onConfirm={() =>
          transferToDelete &&
          delTransfer.mutate(transferToDelete.id, { onSuccess: () => setTransferToDelete(null) })
        }
      />
      <ConfirmDialog
        open={recurringToRegister !== null}
        title={
          (recurringToRegister?.daysUntil ?? 0) > 0
            ? '¿Registrar antes de la fecha?'
            : '¿Registrar este movimiento fijo?'
        }
        description={
          (recurringToRegister?.daysUntil ?? 0) > 0
            ? 'Creará el movimiento real hoy y la automatización no lo repetirá en su fecha programada. Hazlo sólo si ya ocurrió o quieres adelantarlo.'
            : 'Creará ahora el movimiento real y actualizará los saldos correspondientes. La automatización no lo duplicará.'
        }
        itemLabel={
          recurringToRegister
            ? `${recurringToRegister.name} · ${formatMoney(recurringToRegister.amount)} · ${recurringDueLabel(recurringToRegister)}`
            : undefined
        }
        confirmLabel="Registrar ahora"
        pendingLabel="Registrando…"
        pending={generateRecurring.isPending}
        tone="warning"
        footnote="Al terminar podrás usar Deshacer desde la notificación."
        error={generateRecurring.isError ? getApiErrorMessage(generateRecurring.error) : undefined}
        onClose={() => setRecurringToRegister(null)}
        onConfirm={() =>
          recurringToRegister &&
          generateRecurring.mutate(
            { id: recurringToRegister.id, month },
            { onSuccess: () => setRecurringToRegister(null) },
          )
        }
      />
      <ConfirmDialog
        open={plannedToConfirm !== null}
        title={
          plannedToConfirm?.sourceKind === RecurringSourceKind.CARD
            ? '¿Registrar este cargo?'
            : '¿Confirmar este movimiento?'
        }
        description="Se convertirá en un movimiento real y actualizará el saldo de la cuenta o la deuda de la tarjeta."
        itemLabel={
          plannedToConfirm
            ? `${plannedToConfirm.note || 'Movimiento planeado'} · ${formatMoney(plannedToConfirm.amount)}`
            : undefined
        }
        confirmLabel={
          plannedToConfirm?.sourceKind === RecurringSourceKind.CARD
            ? 'Registrar cargo'
            : 'Confirmar movimiento'
        }
        pendingLabel="Registrando…"
        pending={confirmPlanned.isPending}
        tone="warning"
        footnote={false}
        error={confirmPlanned.isError ? getApiErrorMessage(confirmPlanned.error) : undefined}
        onClose={() => setPlannedToConfirm(null)}
        onConfirm={() =>
          plannedToConfirm &&
          confirmPlanned.mutate(
            { id: plannedToConfirm.id, body: {} },
            { onSuccess: () => setPlannedToConfirm(null) },
          )
        }
      />
      <ConfirmDialog
        open={plannedToCancel !== null}
        title="¿Cancelar este pendiente?"
        description="Dejará de aparecer entre tus pendientes y no modificará ningún saldo."
        itemLabel={
          plannedToCancel
            ? `${plannedToCancel.note || 'Movimiento planeado'} · ${formatMoney(plannedToCancel.amount)}`
            : undefined
        }
        confirmLabel="Cancelar pendiente"
        pendingLabel="Cancelando…"
        pending={cancelPlanned.isPending}
        tone="warning"
        footnote="Esta acción no se puede deshacer desde la app."
        error={cancelPlanned.isError ? getApiErrorMessage(cancelPlanned.error) : undefined}
        onClose={() => setPlannedToCancel(null)}
        onConfirm={() =>
          plannedToCancel &&
          cancelPlanned.mutate(plannedToCancel.id, {
            onSuccess: () => setPlannedToCancel(null),
          })
        }
      />
    </div>
  );
}

/** Traspasos del período: se listan aparte porque no son ingresos ni egresos. */
function TransfersList({
  transfers,
  loading,
  accountName,
  onDelete,
}: {
  transfers: TransferItem[];
  loading: boolean;
  accountName: Map<string, string>;
  onDelete: (transfer: TransferItem) => void;
}) {
  if (loading) return <TransactionSkeleton />;
  if (transfers.length === 0) {
    return (
      <EmptyState
        title="No hay traspasos en este período"
        description="Los traspasos aparecen aquí para que no distorsionen tu gasto."
      />
    );
  }
  return (
    <ul className="space-y-2.5">
      {transfers.map((t) => (
        <li
          key={t.id}
          className="flex items-center justify-between rounded-[18px] border border-slate-200/80 bg-white px-4 py-3.5 shadow-[0_1px_2px_rgba(15,23,42,.04),0_12px_28px_-18px_rgba(15,23,42,.16)]"
        >
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-800">
              {accountName.get(t.fromAccountId) ?? 'Cuenta'} →{' '}
              {accountName.get(t.toAccountId) ?? 'Cuenta'}
              {t.note ? ` · ${t.note}` : ''}
            </p>
            <p className="mt-0.5 text-xs text-slate-600">
              {formatDate(t.date)} ·{' '}
              {t.reconciliationProtected
                ? 'conciliación protegida; corrige con un movimiento nuevo'
                : t.recurringExpenseId
                  ? 'movimiento fijo'
                  : 'no cuenta como gasto'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-600">{formatMoney(t.amount)}</span>
            {t.reconciliationProtected ? (
              <span
                className="inline-flex min-h-11 items-center px-1 text-xs font-semibold text-savings"
                title="Este traspaso explica una conciliación. Para corregirlo, registra un hecho nuevo."
              >
                Conciliación
              </span>
            ) : t.recurringExpenseId ? (
              <Tooltip
                content="Cambiar este mes o los próximos desde Movimientos fijos"
                align="end"
              >
                <Link
                  to={`/gastos-fijos?ajustar=${encodeURIComponent(t.recurringExpenseId)}&mes=${t.recurringOccurrenceMonth ?? t.date.slice(0, 7)}`}
                  className="tap-target inline-flex h-9 items-center px-1 text-xs font-semibold text-savings hover:text-blue-700"
                  aria-label={`Cambiar monto del traspaso fijo ${t.note ?? ''}`.trim()}
                >
                  Fijo
                </Link>
              </Tooltip>
            ) : (
              <Tooltip content="Eliminar traspaso" align="end">
                <button
                  onClick={() => onDelete(t)}
                  className="tap-target flex h-11 w-11 items-center justify-center rounded-lg text-slate-600 hover:bg-red-50 hover:text-expense"
                  aria-label="Eliminar traspaso"
                >
                  ×
                </button>
              </Tooltip>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

export function TransactionSkeleton() {
  return (
    <SkeletonState label="Cargando movimientos…" className="space-y-2.5">
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-[76px]" />
      ))}
    </SkeletonState>
  );
}

function TransactionsSummary({
  summary,
  type,
  loading,
  className = '',
}: {
  summary: { income: number; expense: number; net: number; count: number };
  type: TransactionType | undefined;
  loading: boolean;
  className?: string;
}) {
  if (loading) return <Skeleton className={`mb-3 h-[76px] ${className}`} />;
  const showIncome = type !== TransactionType.EXPENSE;
  const showExpense = type !== TransactionType.INCOME;
  return (
    <section
      className={`mb-3 grid grid-cols-2 overflow-hidden rounded-[16px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)] sm:grid-cols-3 ${className}`}
    >
      {showIncome && (
        <SummaryMetric
          label="Ingresos registrados"
          value={formatMoney(summary.income)}
          tone="income"
        />
      )}
      {showExpense && (
        <SummaryMetric
          label="Gastos registrados"
          value={formatMoney(summary.expense)}
          tone="expense"
        />
      )}
      <SummaryMetric
        label={type ? 'Registrados' : 'Diferencia registrada'}
        value={type ? String(summary.count) : formatMoney(summary.net)}
        tone={type ? 'neutral' : summary.net >= 0 ? 'income' : 'expense'}
      />
    </section>
  );
}

function TransactionsDesktopSummary({
  summary,
  loading,
}: {
  summary: { income: number; expense: number; net: number; count: number };
  loading: boolean;
}) {
  if (loading) return <Skeleton className="hidden h-44 lg:block" />;
  return (
    <aside className="hidden rounded-[16px] border border-slate-200/80 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)] lg:block">
      <h2 className="text-[15px] font-semibold text-slate-900">Resumen del mes</h2>
      <dl className="mt-4 space-y-3 text-xs">
        <div>
          <div className="flex justify-between text-slate-500">
            <dt>Ingresos</dt>
            <dd className="font-bold text-income">{formatMoney(summary.income)}</dd>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
            <div className="h-full w-full rounded-full bg-income" />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-slate-500">
            <dt>Gastos</dt>
            <dd className="font-bold text-expense">{formatMoney(summary.expense)}</dd>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-expense"
              style={{
                width: `${summary.income > 0 ? Math.min(100, (summary.expense / summary.income) * 100) : 0}%`,
              }}
            />
          </div>
        </div>
        <div className="flex justify-between border-t border-slate-100 pt-3 text-slate-500">
          <dt>Diferencia</dt>
          <dd className={`font-bold ${summary.net >= 0 ? 'text-income' : 'text-expense'}`}>
            {formatMoney(summary.net)}
          </dd>
        </div>
      </dl>
    </aside>
  );
}

function AccountsDesktopPreview({
  accounts,
  loading,
  error,
  onRetry,
}: {
  accounts: AccountWithBuckets[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  if (loading) return <Skeleton className="h-52" />;

  return (
    <aside
      className="overflow-hidden rounded-[16px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,.06),0_8px_20px_-12px_rgba(15,23,42,.15)]"
      aria-labelledby="account-available-preview-title"
    >
      <div className="p-4 pb-3">
        <h2
          id="account-available-preview-title"
          className="text-[15px] font-semibold text-slate-900"
        >
          Disponible hoy por cuenta
        </h2>
        <p className="mt-1 text-[11px] leading-4 text-slate-500">
          Saldos actuales fuera de tus apartados.
        </p>
      </div>

      {error ? (
        <div className="border-t border-slate-100 px-4 py-3 text-xs" role="alert">
          <p className="leading-5 text-expense">No pudimos cargar los saldos de tus cuentas.</p>
          <button
            type="button"
            className="tap-target mt-1 font-semibold text-savings hover:text-blue-700"
            onClick={onRetry}
          >
            Volver a intentar
          </button>
        </div>
      ) : accounts.length === 0 ? (
        <div className="border-t border-slate-100 px-4 py-3 text-xs text-slate-600">
          <p className="leading-5">Aún no tienes cuentas para registrar movimientos.</p>
        </div>
      ) : (
        <ul className="max-h-[360px] divide-y divide-slate-100 overflow-y-auto border-t border-slate-100 px-4">
          {accounts.map((account) => {
            const reserved = account.buckets.reduce((sum, bucket) => sum + bucket.balance, 0);
            const available = accountAvailableBalance(account);
            return (
              <li key={account.id} className="py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="shrink-0 text-sm" aria-hidden>
                    {ACCOUNT_TYPE_META[account.type].icon}
                  </span>
                  <p
                    className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-800"
                    title={account.name}
                  >
                    {account.name}
                  </p>
                  <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold tracking-[.04em] text-slate-600">
                    {account.currency}
                  </span>
                </div>
                <dl className="ml-6 mt-2 space-y-1 text-[11px]">
                  <div className="flex items-baseline justify-between gap-2">
                    <dt className="font-medium text-slate-500">Disponible</dt>
                    <dd
                      className={`text-sm font-bold tabular-nums ${available < 0 ? 'text-expense' : 'text-savings'}`}
                    >
                      {formatMoney(available, account.currency)}
                    </dd>
                  </div>
                  {reserved > 0 && (
                    <>
                      <div className="flex justify-between gap-2 text-slate-500">
                        <dt>Saldo total</dt>
                        <dd className="font-semibold tabular-nums text-slate-700">
                          {formatMoney(account.balance, account.currency)}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-2 text-slate-500">
                        <dt>Apartados</dt>
                        <dd className="font-semibold tabular-nums text-slate-700">
                          {formatMoney(reserved, account.currency)}
                        </dd>
                      </div>
                    </>
                  )}
                </dl>
              </li>
            );
          })}
        </ul>
      )}

      <Link
        to="/cuentas"
        className="tap-target flex items-center justify-center border-t border-slate-100 px-4 text-xs font-semibold text-savings hover:bg-blue-50 hover:text-blue-700"
      >
        {accounts.length === 0 && !error ? 'Agregar cuenta' : 'Ver cuentas'}
      </Link>
    </aside>
  );
}

function PendingSummary({
  plannedItems,
  recurring,
  loading,
}: {
  plannedItems: PlannedMovement[];
  recurring?: {
    items: UpcomingRecurring[];
    totals: { income: number; expense: number; transfer: number };
  };
  loading: boolean;
}) {
  if (loading) return <Skeleton className="mb-3 h-[76px]" />;
  const plannedIncome = plannedItems
    .filter((item) => item.kind === PlannedMovementKind.INCOME)
    .reduce((sum, item) => sum + item.amount, 0);
  const plannedExpense = plannedItems
    .filter((item) => item.kind === PlannedMovementKind.EXPENSE)
    .reduce((sum, item) => sum + item.amount, 0);
  const plannedTransfer = plannedItems
    .filter((item) => item.kind === PlannedMovementKind.TRANSFER)
    .reduce((sum, item) => sum + item.amount, 0);
  const recurringTotals = recurring?.totals ?? { income: 0, expense: 0, transfer: 0 };
  const recurringCount = recurring?.items.length ?? 0;
  const pendingCount = recurringCount + plannedItems.length;
  return (
    <section className="mb-5 grid grid-cols-2 overflow-hidden rounded-[18px] border border-amber-200/80 bg-amber-50/40 shadow-[0_1px_2px_rgba(15,23,42,.04),0_12px_28px_-18px_rgba(15,23,42,.18)] sm:grid-cols-4">
      <SummaryMetric
        label="Por recibir"
        value={formatMoney(recurringTotals.income + plannedIncome)}
        tone="income"
      />
      <SummaryMetric
        label="Por pagar"
        value={formatMoney(recurringTotals.expense + plannedExpense)}
        tone="expense"
      />
      <SummaryMetric
        label="Apartados por mover"
        value={formatMoney(recurringTotals.transfer + plannedTransfer)}
        tone="savings"
      />
      <SummaryMetric
        label="Pendientes"
        value={String(pendingCount)}
        hint={`${recurringCount} fijos · ${plannedItems.length} únicos`}
        tone="neutral"
      />
    </section>
  );
}

function TransfersSummary({ transfers, loading }: { transfers: TransferItem[]; loading: boolean }) {
  if (loading) return <Skeleton className="mb-3 h-[76px]" />;
  const total = transfers.reduce((sum, transfer) => sum + transfer.amount, 0);
  return (
    <section className="mb-3 grid grid-cols-2 overflow-hidden rounded-[18px] border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,.04),0_12px_28px_-18px_rgba(15,23,42,.18)]">
      <SummaryMetric label="Movido entre cuentas" value={formatMoney(total)} tone="savings" />
      <SummaryMetric label="Traspasos" value={String(transfers.length)} tone="neutral" />
    </section>
  );
}

function SummaryMetric({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone: 'income' | 'expense' | 'savings' | 'neutral';
}) {
  const color =
    tone === 'income'
      ? 'text-income'
      : tone === 'expense'
        ? 'text-expense'
        : tone === 'savings'
          ? 'text-savings'
          : 'text-slate-900';
  return (
    <div className="min-w-0 border-b border-r border-slate-100 px-3.5 py-3 last:border-b-0 last:border-r-0 even:border-r-0 sm:border-b-0 sm:border-r sm:px-5 sm:last:border-r-0">
      <p className="text-xs font-bold uppercase tracking-[.07em] text-slate-600">{label}</p>
      <p
        className={`mt-1 text-[clamp(.875rem,4vw,1.125rem)] font-bold leading-tight tracking-tight tabular-nums ${color}`}
      >
        {value}
      </p>
      {hint && <p className="mt-0.5 text-xs leading-4 text-slate-600">{hint}</p>}
    </div>
  );
}

/**
 * La bandeja une dos fuentes sin mezclarlas: ocurrencias de fijos por registrar
 * y planeados únicos. Ambos son pendientes, pero se completan de forma distinta.
 */
function PendingMovementSections({
  recurringItems,
  recurringLoading,
  plannedItems,
  plannedLoading,
  accountName,
  onRegisterRecurring,
  registeringRecurring,
  onConfirm,
  onCancel,
  onEditPlanned,
  confirmError,
  onCreate,
  month,
}: {
  recurringItems: UpcomingRecurring[];
  recurringLoading: boolean;
  plannedItems: PlannedMovement[];
  plannedLoading: boolean;
  accountName: Map<string, string>;
  onRegisterRecurring: (item: UpcomingRecurring) => void;
  registeringRecurring: boolean;
  onConfirm: (item: PlannedMovement) => void;
  onCancel: (item: PlannedMovement) => void;
  onEditPlanned: (item: PlannedMovement) => void;
  confirmError?: string;
  onCreate: () => void;
  month: string;
}) {
  const loading = recurringLoading || plannedLoading;
  if (loading) return <TransactionSkeleton />;
  if (recurringItems.length === 0 && plannedItems.length === 0) {
    return (
      <EmptyState
        title="No tienes pendientes por confirmar"
        description="Aquí verás tanto tus movimientos fijos del mes como los planeados una sola vez."
        action={<Button onClick={onCreate}>Planear movimiento</Button>}
      />
    );
  }
  return (
    <div className="space-y-6">
      <PendingSectionHeader
        eyebrow="Se repiten cada mes"
        title="Fijos pendientes de este mes"
        description="Cambia sólo el monto de este mes o edita los próximos movimientos. Registrar crea el movimiento real."
        action={
          <Link
            to="/gastos-fijos"
            className="tap-target -mr-2 inline-flex items-center px-2 text-xs font-semibold text-savings hover:text-blue-700"
          >
            Gestionar fijos →
          </Link>
        }
      />
      {recurringItems.length > 0 ? (
        <UpcomingRecurringList
          items={recurringItems}
          month={month}
          onRegister={onRegisterRecurring}
          registering={registeringRecurring}
        />
      ) : (
        <p className="-mt-3 px-1 text-sm text-slate-500">
          No hay movimientos fijos pendientes en este período.
        </p>
      )}

      <PendingSectionHeader
        eyebrow="Ocurren una sola vez"
        title="Planeados únicos"
        description="Confírmalos cuando ocurran; hasta entonces no modifican saldos ni deuda. Una confirmación interrumpida seguirá visible para recuperarla."
        action={
          <button
            type="button"
            onClick={onCreate}
            className="tap-target -mr-2 inline-flex items-center px-2 text-xs font-semibold text-savings hover:text-blue-700"
          >
            + Planear
          </button>
        }
      />
      {plannedItems.length > 0 ? (
        <>
          {plannedItems.some((item) => item.sourceKind === RecurringSourceKind.CARD) && (
            <HelperText className="-mt-3" tone="info">
              <strong>Confirmar</strong> crea el movimiento desde la cuenta elegida.{' '}
              <strong>Registrar cargo</strong> crea el cargo en tu tarjeta y aumenta su deuda; el
              saldo de tu cuenta cambia cuando pagues la tarjeta.
            </HelperText>
          )}
          {confirmError && (
            <p className="-mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
              {confirmError}
            </p>
          )}
          <PlannedList
            items={plannedItems}
            accountName={accountName}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onEdit={onEditPlanned}
          />
        </>
      ) : (
        <p className="-mt-3 px-1 text-sm text-slate-500">
          No tienes movimientos únicos por confirmar.
        </p>
      )}
    </div>
  );
}

function PendingSectionHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action: ReactNode;
}) {
  return (
    <div className="flex items-end justify-between gap-3 px-1">
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold uppercase tracking-[.07em] text-slate-600">{eyebrow}</p>
        <h2 className="mt-0.5 text-sm font-semibold text-slate-800">{title}</h2>
        <p className="mt-0.5 text-xs leading-4 text-slate-500">{description}</p>
      </div>
      <div className="shrink-0 pb-0.5">{action}</div>
    </div>
  );
}

function UpcomingRecurringList({
  items,
  month,
  onRegister,
  registering,
}: {
  items: UpcomingRecurring[];
  month: string;
  onRegister: (item: UpcomingRecurring) => void;
  registering: boolean;
}) {
  const navigate = useNavigate();
  return (
    <ul className="space-y-2.5">
      {items.map((item) => {
        const isIncome = item.direction === RecurringDirection.INCOME;
        const isTransfer = item.direction === RecurringDirection.TRANSFER;
        const tone = isTransfer ? 'text-savings' : isIncome ? 'text-income' : 'text-expense';
        const prefix = isTransfer ? '⇄ ' : isIncome ? '+' : '−';
        return (
          <li
            key={item.id}
            className="relative overflow-hidden rounded-[18px] border border-amber-200/80 border-l-4 border-l-amber-400 bg-amber-50/[.16] px-4 py-3.5 shadow-[0_1px_2px_rgba(15,23,42,.04),0_12px_28px_-18px_rgba(15,23,42,.16)]"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-800">{item.name}</p>
                <p className="mt-0.5 text-xs text-slate-600">
                  {recurringDueLabel(item)} · movimiento fijo
                </p>
              </div>
              <div className="flex w-full items-center gap-1 sm:w-auto sm:shrink-0 sm:justify-end">
                <span className={`mr-auto min-w-0 text-sm font-semibold sm:mr-1 ${tone}`}>
                  {prefix}
                  {formatMoney(item.amount)}
                </span>
                <Tooltip
                  content="Registra este movimiento fijo ahora; la automatización no lo duplicará"
                  align="end"
                >
                  <button
                    type="button"
                    onClick={() => onRegister(item)}
                    disabled={registering}
                    className="tap-target px-1.5 text-xs font-semibold text-savings disabled:opacity-50 sm:px-2"
                  >
                    {registering ? 'Registrando…' : 'Registrar'}
                  </button>
                </Tooltip>
                <ItemActionsMenu
                  itemLabel={item.name}
                  title="Opciones del movimiento"
                  actions={[
                    {
                      label: 'Cambiar monto de este mes',
                      description: 'Modifica únicamente este pendiente.',
                      onSelect: () =>
                        navigate(
                          `/gastos-fijos?ajustar=${encodeURIComponent(item.id)}&mes=${month}`,
                        ),
                    },
                    {
                      label: 'Editar próximos movimientos',
                      description: 'Cambia el monto habitual o la programación futura.',
                      onSelect: () =>
                        navigate(`/gastos-fijos?editar=${encodeURIComponent(item.id)}`),
                    },
                  ]}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function recurringDueLabel(item: UpcomingRecurring): string {
  const formatted = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short' }).format(
    new Date(`${item.dueDate}T12:00:00`),
  );
  if (item.daysUntil < 0) return `Venció el ${formatted}`;
  if (item.daysUntil === 0) return 'Toca hoy';
  if (item.daysUntil === 1) return `Mañana · ${formatted}`;
  return `Esperado el ${formatted}`;
}

/** Planeados únicos: no tocan saldos hasta confirmar su ocurrencia. */
function PlannedList({
  items,
  accountName,
  onConfirm,
  onCancel,
  onEdit,
}: {
  items: PlannedMovement[];
  accountName: Map<string, string>;
  onConfirm: (item: PlannedMovement) => void;
  onCancel: (item: PlannedMovement) => void;
  onEdit: (item: PlannedMovement) => void;
}) {
  const hoy = toDateInputValue();
  return (
    <ul className="space-y-2.5">
      {items.map((p) => {
        const pendiente = p.status === PlannedMovementStatus.PLANNED;
        const confirmando = p.status === PlannedMovementStatus.CONFIRMING;
        const vencido = pendiente && p.expectedDate.slice(0, 10) < hoy;
        const signo =
          p.kind === PlannedMovementKind.INCOME
            ? '+'
            : p.kind === PlannedMovementKind.TRANSFER
              ? '⇄'
              : '−';
        const color =
          p.kind === PlannedMovementKind.INCOME
            ? 'text-income'
            : p.kind === PlannedMovementKind.TRANSFER
              ? 'text-savings'
              : 'text-expense';
        return (
          <li
            key={p.id}
            className="ui-card flex flex-col gap-2 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">
                {p.note || 'Movimiento planeado'}
              </p>
              <p className="mt-0.5 text-xs text-slate-600">
                {formatDate(p.expectedDate)} ·{' '}
                {p.sourceKind === RecurringSourceKind.CARD
                  ? 'Tarjeta de crédito'
                  : (accountName.get(p.accountId ?? '') ?? 'Cuenta')}
                {p.sourceKind === RecurringSourceKind.CARD && ' · cargo de contado al confirmar'}
                {vencido && ' · venció, sigue esperando'}
                {confirmando && ' · confirmación en revisión'}
              </p>
            </div>
            <div className="flex w-full items-center gap-1 sm:w-auto sm:shrink-0 sm:justify-end sm:gap-2">
              <span
                className={`mr-auto min-w-0 text-sm font-semibold sm:mr-1 ${pendiente ? color : 'text-slate-600'}`}
              >
                {signo}
                {formatMoney(p.amount)}
              </span>
              {pendiente && (
                <>
                  <Tooltip
                    content={
                      p.sourceKind === RecurringSourceKind.CARD
                        ? 'Confirma que ya ocurrió y crea el cargo en la tarjeta'
                        : 'Confirma que ya ocurrió y actualiza el saldo de la cuenta'
                    }
                    align="end"
                  >
                    <button
                      onClick={() => onConfirm(p)}
                      className="tap-target px-1.5 text-xs font-medium text-savings sm:px-2"
                      aria-label={
                        p.sourceKind === RecurringSourceKind.CARD
                          ? `Registrar cargo ${p.note || 'planeado'}`
                          : `Confirmar ${p.note || 'movimiento planeado'}`
                      }
                    >
                      Registrar
                    </button>
                  </Tooltip>
                  <ItemActionsMenu
                    itemLabel={p.note || 'Movimiento planeado'}
                    title="Opciones del pendiente"
                    actions={[
                      {
                        label: 'Editar pendiente',
                        description: 'Cambia sus datos antes de registrarlo.',
                        onSelect: () => onEdit(p),
                      },
                      {
                        label: 'Cancelar pendiente',
                        description: 'No modificará tus saldos.',
                        tone: 'danger',
                        onSelect: () => onCancel(p),
                      },
                    ]}
                  />
                </>
              )}
              {confirmando && (
                <button
                  onClick={() => onConfirm(p)}
                  className="tap-target px-1.5 text-xs font-medium text-savings sm:px-2"
                >
                  Recuperar
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
